import { db } from "./dbClient";

import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";

// KYC periodic refresh thresholds (F-004 / E-011)
const KYC_REFRESH_DAYS = { High: 180, Medium: 365, Low: 365 };

function isKYCRefreshOverdue(customer) {
  if (!customer?.lastScreened) return false;
  const days = KYC_REFRESH_DAYS[customer.riskLevel] || 365;
  const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return new Date(customer.lastScreened) < cutoff;
}

// ─── DILISENSE API ────────────────────────────────────────────────────────────
// Docs: https://developers.dilisense.com
// Endpoints used:
//   GET https://api.dilisense.com/v1/checkIndividual?names=...&fuzzy_search=1
//   GET https://api.dilisense.com/v1/checkEntity?names=...&fuzzy_search=1
// Header: x-api-key: <your key>
// Response: { total_hits: number, found_records: [...] }
//   Each record has source_type: "SANCTION" | "PEP" | "CRIMINAL"

// E-037 / F-024: Dilisense calls routed through Vercel serverless functions

// Rate limiter for Dilisense API with persistent rate limit tracking
// Based on observed API behavior, using aggressive 30-second intervals
const dilisenseRequestQueue = [];
const dilisenseRequestCache = new Map();
let dilisenseIsProcessing = false;
let lastDilisenseRequestTime = 0;
let dilisenseRateLimitUntil = 0;

// Load rate limit state from localStorage to persist across page reloads
function loadDilisenseRateLimitState() {
  try {
    const stored = localStorage.getItem('dilisense_rate_limit_until');
    if (stored) {
      const until = parseInt(stored);
      if (until > Date.now()) {
        dilisenseRateLimitUntil = until;
      } else {
        localStorage.removeItem('dilisense_rate_limit_until');
      }
    }
  } catch (e) {
    console.warn('[Dilisense] Could not load rate limit state:', e);
  }
}

function saveDilisenseRateLimitState() {
  try {
    if (dilisenseRateLimitUntil > Date.now()) {
      localStorage.setItem('dilisense_rate_limit_until', String(dilisenseRateLimitUntil));
    }
  } catch (e) {
    console.warn('[Dilisense] Could not save rate limit state:', e);
  }
}

function getDilisenseTimeUntilNextRequest() {
  const now = Date.now();
  const rateLimitWait = Math.max(0, dilisenseRateLimitUntil - now);
  const MIN_REQUEST_INTERVAL_MS = 30000; // 30 seconds - very conservative
  const timeSinceLastRequest = now - lastDilisenseRequestTime;
  const intervalWait = Math.max(0, MIN_REQUEST_INTERVAL_MS - timeSinceLastRequest);
  return Math.max(rateLimitWait, intervalWait);
}

function isDilisenseRateLimited() {
  return getDilisenseTimeUntilNextRequest() > 0;
}

async function processDilisenseQueue() {
  if (dilisenseIsProcessing || dilisenseRequestQueue.length === 0) return;
  dilisenseIsProcessing = true;

  while (dilisenseRequestQueue.length > 0) {
    // Check rate limit before processing
    const waitTime = getDilisenseTimeUntilNextRequest();
    if (waitTime > 0) {
      console.log(`[Dilisense] Rate limit active, waiting ${waitTime}ms`);
      await new Promise(r => setTimeout(r, Math.min(waitTime, 1000)));
      // Re-check queue in case we should stop
      if (dilisenseRequestQueue.length === 0) {
        dilisenseIsProcessing = false;
        return;
      }
    }

    const { resolve, reject, fn, cacheKey } = dilisenseRequestQueue.shift();

    try {
      // Check cache first
      if (cacheKey && dilisenseRequestCache.has(cacheKey)) {
        console.log(`[Dilisense] Using cached result for ${cacheKey}`);
        const cachedResult = dilisenseRequestCache.get(cacheKey);
        resolve(cachedResult);
        continue;
      }

      lastDilisenseRequestTime = Date.now();
      const result = await fn();

      // Cache successful results for 10 minutes
      if (cacheKey) {
        dilisenseRequestCache.set(cacheKey, result);
        setTimeout(() => dilisenseRequestCache.delete(cacheKey), 600000);
      }

      resolve(result);
    } catch (err) {
      reject(err);
    }
  }

  dilisenseIsProcessing = false;
}

async function queueDilisenseRequest(fn, cacheKey) {
  return new Promise((resolve, reject) => {
    dilisenseRequestQueue.push({ resolve, reject, fn, cacheKey });
    processDilisenseQueue();
  });
}

// Initialize rate limit state on module load
loadDilisenseRateLimitState();

async function dilisenseCheck(name, type, _apiKey) {
  // Choose endpoint based on customer type
  const endpoint = type === "Business" || type === "Entity" ? "entity" : "individual";
  const url = `/api/dilisense/${endpoint}?names=${encodeURIComponent(name)}&fuzzy_search=1`;
  const cacheKey = `dilisense_${endpoint}_${name.toLowerCase()}`;

  // Queue request to avoid rate limiting (with cache to prevent duplicate requests)
  return queueDilisenseRequest(async () => {
    try {
      const resp = await fetch(url, {
        method: "GET",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });

      // Handle specific errors
      if (resp.status === 401) throw new Error("Dilisense API key not configured. Add DILISENSE_API_KEY to Vercel environment.");
      if (resp.status === 400) throw new Error("Bad request — check the customer name");

      if (resp.status === 429) {
        // Rate limited - update rate limit state for 2 minutes
        dilisenseRateLimitUntil = Date.now() + 120000; // 2 minutes
        saveDilisenseRateLimitState();
        console.warn(`[Dilisense] Rate limited! API unavailable until ${new Date(dilisenseRateLimitUntil).toLocaleTimeString()}`);
        throw new Error("Dilisense API rate limit exceeded. Please wait 2 minutes before trying again.");
      }

      if (resp.status === 500) throw new Error("Server error — Dilisense API key may not be configured");
      if (!resp.ok) throw new Error(`Dilisense API error: ${resp.status}`);

      // Clear rate limit on success
      dilisenseRateLimitUntil = 0;
      saveDilisenseRateLimitState();

      return resp.json();
    } catch (err) {
      throw err;
    }
  }, cacheKey);
}

function parseResults(data) {
  // Classify hits by source_type
  const records = data.found_records || [];
  const sanctions = records.filter(r => r.source_type === "SANCTION");
  const pep       = records.filter(r => r.source_type === "PEP");
  const criminal  = records.filter(r => r.source_type === "CRIMINAL");

  return {
    totalHits: data.total_hits || 0,
    sanctions,
    pep,
    criminal,
    allRecords: records,
    // Status per check type
    sanctionsStatus:  sanctions.length  > 0 ? "failed" : "passed",
    watchlistStatus:  pep.length        > 0 ? "failed" : "passed",
    criminalStatus:   criminal.length   > 0 ? "failed" : "passed",
  };
}

// ─── NAMSOR API ───────────────────────────────────────────────────────────────
// Docs: https://v2.namsor.com/NamSorAPIv2/apidoc.html
// Endpoint: GET /api2/json/originFull/{firstName}/{lastName}
// Header: X-API-KEY: <key>

// FATF blacklist (highest risk) — Iran, North Korea, Myanmar
const FATF_BLACKLIST = new Set(["IR", "KP", "MM"]);

// FATF grey list (increased monitoring, March 2024)
const FATF_GREYLIST = new Set([
  "AL", "AO", "BB", "BF", "BJ", "BG", "CM", "CD", "HR", "GA",
  "GH", "GY", "HT", "JM", "KH", "ML", "MZ", "NA", "NI", "NG",
  "PA", "PK", "PH", "SN", "ZA", "SS", "SY", "TJ", "TZ", "TT",
  "TR", "UG", "AE", "UZ", "VN", "YE",
]);

// Country code → display name for common codes
const COUNTRY_NAMES = {
  AF:"Afghanistan",AL:"Albania",DZ:"Algeria",AO:"Angola",AR:"Argentina",AU:"Australia",
  AZ:"Azerbaijan",BB:"Barbados",BY:"Belarus",BE:"Belgium",BZ:"Belize",BJ:"Benin",
  BO:"Bolivia",BA:"Bosnia",BW:"Botswana",BR:"Brazil",BG:"Bulgaria",BF:"Burkina Faso",
  KH:"Cambodia",CM:"Cameroon",CA:"Canada",TD:"Chad",CL:"Chile",CN:"China",CO:"Colombia",
  CG:"Congo",HR:"Croatia",CU:"Cuba",CD:"DRC",DK:"Denmark",EG:"Egypt",ET:"Ethiopia",
  FI:"Finland",FR:"France",GA:"Gabon",GM:"Gambia",GE:"Georgia",DE:"Germany",GH:"Ghana",
  GR:"Greece",GT:"Guatemala",GN:"Guinea",GY:"Guyana",HT:"Haiti",HN:"Honduras",HU:"Hungary",
  IN:"India",ID:"Indonesia",IR:"Iran",IQ:"Iraq",IE:"Ireland",IL:"Israel",IT:"Italy",
  JM:"Jamaica",JP:"Japan",JO:"Jordan",KZ:"Kazakhstan",KE:"Kenya",KP:"North Korea",
  KR:"South Korea",KW:"Kuwait",LB:"Lebanon",LY:"Libya",LT:"Lithuania",MK:"North Macedonia",
  MY:"Malaysia",ML:"Mali",MT:"Malta",MX:"Mexico",MD:"Moldova",MN:"Mongolia",MA:"Morocco",
  MZ:"Mozambique",MM:"Myanmar",NA:"Namibia",NP:"Nepal",NL:"Netherlands",NZ:"New Zealand",
  NI:"Nicaragua",NG:"Nigeria",NO:"Norway",OM:"Oman",PK:"Pakistan",PA:"Panama",PY:"Paraguay",
  PE:"Peru",PH:"Philippines",PL:"Poland",PT:"Portugal",QA:"Qatar",RO:"Romania",RU:"Russia",
  SA:"Saudi Arabia",SN:"Senegal",RS:"Serbia",SL:"Sierra Leone",ZA:"South Africa",
  SS:"South Sudan",ES:"Spain",LK:"Sri Lanka",SD:"Sudan",SY:"Syria",TJ:"Tajikistan",
  TZ:"Tanzania",TH:"Thailand",TT:"Trinidad & Tobago",TN:"Tunisia",TR:"Turkey",
  TM:"Turkmenistan",UG:"Uganda",UA:"Ukraine",AE:"UAE",GB:"United Kingdom",US:"United States",
  UY:"Uruguay",UZ:"Uzbekistan",VE:"Venezuela",VN:"Vietnam",YE:"Yemen",ZM:"Zambia",ZW:"Zimbabwe",
};

function parseName(fullName) {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

async function namsorCheck(fullName) {
  const { firstName, lastName } = parseName(fullName);
  try {
    const resp = await fetch("/api/namsor", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ firstName, lastName: lastName || "-" }),
    });
    if (resp.status === 500) throw new Error("NamSor API key not configured");
    if (!resp.ok) throw new Error(`NamSor error: ${resp.status}`);
    const data = await resp.json();
    const origin = data.countryOrigin || null;
    return {
      countryOrigin:    origin,
      countryOriginAlt: data.countryOriginAlt || null,
      score:            data.score || 0,
      isFATFBlacklist:  FATF_BLACKLIST.has(origin),
      isFATFHighRisk:   FATF_GREYLIST.has(origin) || FATF_BLACKLIST.has(origin),
    };
  } catch (err) {
    console.warn("[namsor] error:", err.message);
    throw err;
  }
}

// ─── AI RISK SYNTHESIS ────────────────────────────────────────────────────────
async function aiRiskSynthesis(customerName, sanctionsData, nameOrigin) {
  const proxyUrl = `${import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001"}/api/ai`;
  const originName = nameOrigin.countryOrigin || "Unknown";
  const fatfStatus = nameOrigin.isFATFBlacklist
    ? "FATF Blacklist (highest risk)"
    : nameOrigin.isFATFHighRisk
    ? "FATF Grey List (increased monitoring)"
    : "Not on FATF lists";

  const sanctionHits  = (sanctionsData?.sanctions  || []).map(m => m.name || m.entity_name).join(", ") || "None";
  const pepHits      = (sanctionsData?.watchlist || sanctionsData?.pep || []).map(m => m.name || m.entity_name).join(", ") || "None";
  const criminalHits = (sanctionsData?.criminal  || []).map(m => m.name || m.entity_name).join(", ") || "None";
  const totalHits    = (sanctionsData?.sanctions?.length || 0)
                     + ((sanctionsData?.watchlist || sanctionsData?.pep)?.length || 0)
                     + (sanctionsData?.criminal?.length || 0);

  const prompt = `You are a senior AML compliance analyst. Synthesize the following screening results for subject "${customerName}" and provide a concise risk assessment.\n\nSANCTIONS SCREENING:\n- Sanctions list hits: ${sanctionHits}\n- PEP/Watchlist hits: ${pepHits}\n- Criminal record hits: ${criminalHits}\n- Total hits: ${totalHits}\n\nNAME INTELLIGENCE (NamSor):\n- Predicted country of name origin: ${originName} (${nameOrigin.countryOrigin || "?"})\n- Confidence: ${Math.round((nameOrigin.score || 0) * 100)}%\n- FATF status of origin country: ${fatfStatus}\n\nProvide a 2–3 sentence risk assessment that considers whether the combination of screening results and name origin is suspicious, what the AML risk level is, and what action (if any) is recommended. Be specific about why the combination matters — do not just restate the facts.`;
  
  const resp = await fetch(proxyUrl, {
    method: "POST",
    signal: AbortSignal.timeout(60000),
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({
      system: "You are a senior AML/KYC compliance analyst. Be concise, direct, and actionable.",
      messages: [{ role: "user", content: prompt }],
      max_tokens: 200,
    }),
  });
  if (!resp.ok) throw new Error("AI synthesis failed");
  const data = await resp.json();
  return data.content?.[0]?.text || data.choices?.[0]?.message?.content || "";
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", disabled = false, small = false, style: extraStyle = {} }) {
  const C = useTheme();
  const base = {
    primary: { background: C.accent, color: C.bg, border: "none" },
    ghost:   { background: "transparent", color: C.textMid, border: `1px solid ${C.border}` },
    danger:  { background: "rgba(255,68,68,0.1)", color: C.danger, border: `1px solid ${C.danger}` },
    success: { background: "rgba(0,230,118,0.1)", color: C.success, border: `1px solid ${C.success}` },
    subtle:  { background: C.surfaceAlt, color: C.textMid, border: `1px solid ${C.border}` },
    warn:    { background: "rgba(255,140,0,0.1)", color: C.warn, border: `1px solid ${C.warn}` },
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, padding: small ? "5px 12px" : "9px 18px", borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "'DM Mono', monospace", fontWeight: 600, fontSize: small ? 11 : 13, opacity: disabled ? 0.45 : 1, transition: "opacity 0.15s, transform 0.1s", letterSpacing: "0.03em", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", ...extraStyle }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
    >{children}</button>
  );
}

function Badge({ text, type = "default" }) {
  const C = useTheme();
  const styles = {
    pending:  { bg: "rgba(255,140,0,0.1)",   color: C.warn,    border: `1px solid rgba(255,140,0,0.35)` },
    passed:   { bg: "rgba(0,230,118,0.1)",   color: C.success, border: `1px solid rgba(0,230,118,0.35)` },
    failed:   { bg: "rgba(255,68,68,0.1)",   color: C.danger,  border: `1px solid rgba(255,68,68,0.35)` },
    default:  { bg: "rgba(138,155,181,0.1)", color: C.textMid, border: `1px solid ${C.border}` },
    scanning: { bg: "rgba(0,212,255,0.08)",  color: C.accent,  border: `1px solid rgba(0,212,255,0.3)` },
  }[type] || { bg: "rgba(138,155,181,0.1)", color: C.textMid, border: `1px solid ${C.border}` };
  return (
    <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, background: styles.bg, color: styles.color, border: styles.border, fontFamily: "'DM Mono', monospace", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {type === "scanning" && <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: C.accent, animation: "kycPulse 1s infinite" }} />}
      {text}
    </span>
  );
}

function RiskBadge({ level }) {
  const C = useTheme();
  const map = {
    Low:    { color: C.success, bg: "rgba(0,230,118,0.1)",  border: "rgba(0,230,118,0.3)" },
    Medium: { color: C.warn,    bg: "rgba(255,140,0,0.1)",  border: "rgba(255,140,0,0.3)" },
    High:   { color: C.danger,  bg: "rgba(255,68,68,0.1)",  border: "rgba(255,68,68,0.3)" },
  };
  const s = map[level] || map.Medium;
  return (
    <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>{level}</span>
  );
}

// ─── MATCH DETAIL CARD ─────────────────────────────────────────────────────────
function MatchCard({ record }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const typeColors = {
    SANCTION: { color: C.danger, bg: "rgba(255,68,68,0.1)", label: "⚠ SANCTION" },
    PEP:      { color: C.warn,   bg: "rgba(255,140,0,0.1)", label: "★ PEP" },
    CRIMINAL: { color: "#FF6B6B",bg: "rgba(255,68,68,0.15)",label: "✕ CRIMINAL" },
  }[record.source_type] || { color: C.textMid, bg: C.surfaceAlt, label: record.source_type };

  return (
    <div style={{ background: C.bg, border: `1px solid ${record.source_type === "SANCTION" ? "rgba(255,68,68,0.35)" : record.source_type === "PEP" ? "rgba(255,140,0,0.35)" : C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: typeColors.bg, color: typeColors.color, fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>{typeColors.label}</span>
            {record.pep_type && <span style={{ fontSize: 10, color: C.textDim }}>{record.pep_type.replace(/_/g, " ")}</span>}
          </div>
          <div style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{record.name}</div>
          {record.alias_names?.length > 0 && (
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{t('kyc.match.alsoKnownAs')} {record.alias_names.slice(0, 3).join(", ")}</div>
          )}
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{t('kyc.match.source')} <span style={{ color: C.textMid }}>{record.source_id}</span></div>
        </div>
        <button onClick={() => setExpanded(v => !v)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.textMid, padding: "3px 8px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>
          {expanded ? t('common.less') : t('common.details')}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
          {record.date_of_birth?.length > 0 && <div><span style={{ color: C.textDim }}>📅 DOB: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.date_of_birth.join(", ")}</span></div>}
          {record.citizenship?.length > 0 && <div><span style={{ color: C.textDim }}>🏳️ Nationality: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.citizenship.join(", ")}</span></div>}
          {record.place_of_birth?.length > 0 && <div><span style={{ color: C.textDim }}>📍 Place of Birth: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.place_of_birth.join(", ")}</span></div>}
          {record.nationality?.length > 0 && <div><span style={{ color: C.textDim }}>🌍 Country: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.nationality.join(", ")}</span></div>}
          {record.gender && <div><span style={{ color: C.textDim }}>⚧ Gender: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.gender}</span></div>}
          {record.passport_number && <div><span style={{ color: C.textDim }}>🛂 Passport: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.passport_number}</span></div>}
          {record.national_id && <div><span style={{ color: C.textDim }}>🆔 National ID: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.national_id}</span></div>}
          {record.title && <div><span style={{ color: C.textDim }}>👔 Title: </span><span style={{ color: C.text, fontWeight: 500 }}>{record.title}</span></div>}
          {record.positions?.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ color: C.textDim, marginBottom: 4, fontWeight: 600 }}>💼 Positions:</div>
              {record.positions.slice(0, 4).map((p, i) => <div key={i} style={{ color: C.textMid, fontSize: 11, paddingLeft: 8, borderLeft: `2px solid ${C.border}`, marginBottom: 2 }}>{p}</div>)}
            </div>
          )}
          {record.sanction_details?.length > 0 && (
            <div style={{ gridColumn: "1 / -1", background: "rgba(255,68,68,0.07)", padding: "8px 12px", borderRadius: 6, border: `1px solid rgba(255,68,68,0.2)` }}>
              <div style={{ color: C.danger, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>⚠️ Sanction Details:</div>
              {record.sanction_details.map((d, i) => <div key={i} style={{ color: C.textMid, fontSize: 11 }}>{d}</div>)}
            </div>
          )}
          {record.description?.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ color: C.textDim, fontSize: 11 }}>📝 {record.description.join(" · ")}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CUSTOMER DRAWER ──────────────────────────────────────────────────────────
function CustomerDrawer({ customer, onClose, onSave, onRunScreening, onRunNameOrigin, onReviewHit, onSavePepRelationship, scanningId, checkingNameOriginId, narrative, setNarrative, saveNarrative, narrativeSaved, dilisenseRateLimited, dilisenseTimeUntilAvailable }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...customer });
  const scanning = scanningId === customer.id;
  const allPassed = customer.sanctions === "passed" && customer.watchlist === "passed" && customer.criminal === "passed";
  const anyFailed = customer.sanctions === "failed" || customer.watchlist === "failed" || customer.criminal === "failed";

  const checks = [
    { key: "sanctions",  statusKey: "sanctions",  label: t('kyc.checks.sanctions'),  desc: t('kyc.checks.sanctionsDesc') },
    { key: "watchlist",  statusKey: "watchlist",  label: t('kyc.checks.watchlist'),   desc: t('kyc.checks.watchlistDesc') },
    { key: "criminal",   statusKey: "criminal",   label: t('kyc.checks.criminal'),    desc: t('kyc.checks.criminalDesc') },
  ];

  // Show real match records if available
  const matchData = customer.matchData || {};

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "stretch", justifyContent: "flex-end" }} onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", width: 520, background: C.surface, borderLeft: `1px solid ${C.border}`, display: "flex", flexDirection: "column", overflowY: "auto", zIndex: 1 }}>

        {/* Header */}
        <div style={{ padding: "24px 28px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.12em", marginBottom: 4 }}>{t('kyc.customerProfile')}</div>
            <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 700 }}>{customer.name}</div>
            <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
              <RiskBadge level={customer.riskLevel} />
              {anyFailed  && <Badge text="⚠ MATCH FOUND" type="failed" />}
              {allPassed && customer.sanctions !== "pending" && <Badge text="✓ CLEAR" type="passed" />}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 22 }}>✕</button>
            <button onClick={() => {
              const checkRows = checks.map(c => `<tr><td>${c.label}</td><td style="font-weight:600;color:${customer[c.statusKey]==="passed"?"#276749":customer[c.statusKey]==="failed"?"#C53030":"#744210"}">${(customer[c.statusKey]||"pending").toUpperCase()}</td><td>${c.desc}</td></tr>`).join("");
              const matchRows = Object.entries(matchData).flatMap(([type, items]) => (items||[]).map(m => `<tr><td>${type}</td><td>${m.name||"—"}</td><td>${m.country||"—"}</td><td>${m.score!=null?m.score+"%":"—"}</td></tr>`)).join("");
              const w = window.open("","_blank");
              if(!w || !w.document) return;
              w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>KYC — ${customer.name}</title><style>
              @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@700;800&display=swap');
              *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Mono',monospace;background:#fff;color:#0A2342;padding:12mm;font-size:11px}
              .header{display:flex;align-items:center;gap:10px;margin-bottom:6mm;padding-bottom:4mm;border-bottom:3px solid #00D4FF}
              .logo-hex{width:26px;height:26px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);flex-shrink:0}
              .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:16px}
              .doc-title{font-family:'Syne',sans-serif;font-size:18px;font-weight:700;margin-bottom:1mm}
              .doc-meta{font-size:9px;color:#4A5568;margin-bottom:5mm}
              .section{margin-bottom:5mm}.section-title{font-family:'Syne',sans-serif;font-weight:700;font-size:11px;letter-spacing:0.1em;margin-bottom:2mm;padding-bottom:1mm;border-bottom:1px solid #E2E8F0}
              table{width:100%;border-collapse:collapse;font-size:10px}th{background:#0A2342;color:#fff;padding:5px 7px;text-align:left;font-size:9px}
              td{padding:4px 7px;border-bottom:1px solid #E2E8F0;vertical-align:top}tr:nth-child(even) td{background:#F7FAFC}
              .footer{margin-top:6mm;padding-top:2mm;border-top:1px solid #E2E8F0;font-size:8px;color:#8A9BB5;display:flex;justify-content:space-between}
              @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
              </style></head><body>
              <div class="header"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
              <div class="doc-title">Sanctions Screening — ${customer.name}</div>
              <div class="doc-meta">Generated: ${new Date().toLocaleString()} · Risk: ${customer.riskLevel||"—"} · Type: ${customer.type||"—"}</div>
              <div class="section"><div class="section-title">CUSTOMER DETAILS</div><table><tbody>
                <tr><td><b>Name</b></td><td>${customer.name||"—"}</td><td><b>Type</b></td><td>${customer.type||"—"}</td></tr>
                <tr><td><b>Country</b></td><td>${customer.country||"—"}</td><td><b>Risk</b></td><td>${customer.riskLevel||"—"}</td></tr>
                <tr><td><b>Date Added</b></td><td>${customer.dateAdded||"—"}</td><td><b>Hits</b></td><td>${customer.totalHits||0}</td></tr>
              </tbody></table></div>
              <div class="section"><div class="section-title">SCREENING RESULTS</div><table><thead><tr><th>Check</th><th>Status</th><th>Description</th></tr></thead><tbody>${checkRows}</tbody></table></div>
              ${matchRows?`<div class="section"><div class="section-title">MATCHES</div><table><thead><tr><th>Type</th><th>Name</th><th>Country</th><th>Score</th></tr></thead><tbody>${matchRows}</tbody></table></div>`:""}
              <div class="footer"><span>CrossRegGuard — Confidential</span><span>KYC ${customer.id||""} · ${new Date().toLocaleDateString()}</span></div>
              </body></html>`);
              w.document.close(); setTimeout(()=>w.print(),800);
            }} style={{ background:"transparent", border:"1px solid #00D4FF", borderRadius:6, color:"#00D4FF", padding:"4px 11px", cursor:"pointer", fontSize:11, fontFamily:"'DM Mono',monospace" }}>⬇ PDF</button>
          </div>
        </div>

        <div style={{ padding: "24px 28px", flex: 1, display: "flex", flexDirection: "column", gap: 22 }}>

          {/* Editable fields */}
          <div>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", marginBottom: 12, fontWeight: 600 }}>{t('kyc.customerDetails')}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {[
                { label: t('kyc.fields.fullName'), key: "name", full: true },
                { label: t('kyc.fields.type'), key: "type", type: "select", options: [t('kyc.fields.individual'), t('kyc.fields.business'), t('kyc.fields.entity')] },
                { label: t('kyc.fields.country'), key: "country" },
                { label: t('kyc.fields.riskLevel'), key: "riskLevel", type: "select", options: [t('kyc.riskLevels.low'), t('kyc.riskLevels.medium'), t('kyc.riskLevels.high')] },
                { label: t('kyc.fields.dateAdded'), key: "dateAdded", disabled: true },
              ].map(field => (
                <div key={field.key} style={{ gridColumn: field.full ? "1 / -1" : undefined }}>
                  <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{field.label.toUpperCase()}</div>
                  {field.type === "select" ? (
                    <select value={form[field.key] || ""} onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                      style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "8px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}>
                      {field.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={form[field.key] || ""} disabled={field.disabled} onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                      style={{ width: "100%", background: field.disabled ? "transparent" : C.bg, border: `1px solid ${field.disabled ? "transparent" : C.border}`, borderRadius: 6, color: field.disabled ? C.textDim : C.text, padding: "8px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                  )}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 5 }}>{t('kyc.fields.notes').toUpperCase()}</div>
              <textarea value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2}
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "8px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", resize: "vertical", boxSizing: "border-box" }} />
            </div>
            <div style={{ marginTop: 12 }}>
              <Btn onClick={() => onSave(form)} style={{ flex: 1 }}>{t('kyc.saveChanges')}</Btn>
            </div>
          </div>

          {/* Screening Results */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600 }}>{t('kyc.complianceScreening')}</div>
              <Btn small variant={scanning ? "subtle" : "primary"} disabled={scanning} onClick={() => onRunScreening(customer.id)}>
                {scanning ? t('common.screening') : t('common.runChecks')}
              </Btn>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {checks.map(check => {
                const val = customer[check.statusKey];
                const badgeType = scanning ? "scanning" : (val === "passed" ? "passed" : val === "failed" ? "failed" : "pending");
                const matchRecords = matchData[check.key] || [];
                return (
                  <div key={check.key} style={{ background: C.bg, border: `1px solid ${val === "failed" ? "rgba(255,68,68,0.3)" : C.border}`, borderRadius: 8, padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{check.label}</div>
                        <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{check.desc}</div>
                        {matchRecords.length > 0 && !scanning && (
                          <div style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{t('kyc.drawer.matchCount', { count: matchRecords.length })}</div>
                        )}
                      </div>
                      <Badge text={scanning ? t('kyc.drawer.scanning') : val} type={badgeType} />
                    </div>
                    {/* Show match records inline */}
                    {matchRecords.length > 0 && !scanning && (
                      <div style={{ marginTop: 10 }}>
                        {matchRecords.map((r, i) => <MatchCard key={i} record={r} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Last screened */}
            {customer.lastScreened && (
              <div style={{ color: C.textDim, fontSize: 11, marginBottom: 10 }}>
                {t('kyc.drawer.lastScreened', { date: new Date(customer.lastScreened).toLocaleString() })}
                {customer.totalHits > 0 && <span style={{ color: C.danger, marginLeft: 8 }}>· {t('kyc.drawer.totalHits', { count: customer.totalHits })}</span>}
              </div>
            )}

            {/* Outcome banners */}
            {anyFailed && !scanning && (
              <div style={{ padding: "14px 16px", background: "rgba(255,68,68,0.07)", border: `1px solid rgba(255,68,68,0.25)`, borderRadius: 8 }}>
                <div style={{ color: C.danger, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t('kyc.drawer.matchFoundTitle')}</div>
                <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>{t('kyc.drawer.matchFoundBody')}</div>
              </div>
            )}
            {allPassed && !scanning && customer.sanctions !== "pending" && (
              <div style={{ padding: "14px 16px", background: "rgba(0,230,118,0.07)", border: `1px solid rgba(0,230,118,0.25)`, borderRadius: 8 }}>
                <div style={{ color: C.success, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t('kyc.drawer.allClearTitle')}</div>
                <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>{t('kyc.drawer.allClearBody')}</div>
              </div>
            )}

            {/* F-005: Hit review actions — shown when there are hits and not yet reviewed */}
            {customer.totalHits > 0 && !scanning && !customer.reviewStatus && (
              <div style={{ padding: "14px 16px", background: "rgba(255,140,0,0.07)", border: "1px solid rgba(255,140,0,0.3)", borderRadius: 8, marginTop: 10 }}>
                <div style={{ color: C.warn, fontSize: 12, fontWeight: 700, marginBottom: 10 }}>⚑ Alert Review Required</div>
                <div style={{ color: C.textMid, fontSize: 12, marginBottom: 12 }}>Review the match records above and classify this alert to close it.</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn small variant="danger" onClick={() => onReviewHit(customer, "confirmed")}>✓ Confirm Hit (True Alert)</Btn>
                  <Btn small variant="ghost" onClick={() => onReviewHit(customer, "false_positive")}>✕ Mark as False Positive</Btn>
                </div>
              </div>
            )}

            {/* Show current review status badge */}
            {customer.reviewStatus && !scanning && (
              <div style={{ marginTop: 10, padding: "10px 14px", background: customer.reviewStatus === "confirmed" ? "rgba(255,68,68,0.07)" : "rgba(0,230,118,0.07)", border: `1px solid ${customer.reviewStatus === "confirmed" ? "rgba(255,68,68,0.25)" : "rgba(0,230,118,0.25)"}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <span style={{ color: customer.reviewStatus === "confirmed" ? C.danger : C.success, fontSize: 12, fontWeight: 700 }}>
                    {customer.reviewStatus === "confirmed" ? "⚠ True Alert — Confirmed Hit" : "✓ False Positive — Dismissed"}
                  </span>
                  {customer.reviewedAt && (
                    <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
                      Reviewed: {new Date(customer.reviewedAt).toLocaleString()}
                    </div>
                  )}
                </div>
                <Btn small variant="ghost" onClick={() => onReviewHit(customer, null)}>Reset</Btn>
              </div>
            )}
          </div>

          {/* F-006: PEP Relationship Classification (FATF R.12) */}
          {(customer.watchlist === "failed" || customer.pepRelationship) && (
            <div>
              <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 10 }}>PEP RELATIONSHIP — FATF R.12</div>
              <div style={{ background: C.bg, border: `1px solid rgba(255,140,0,0.3)`, borderRadius: 8, padding: "12px 16px" }}>
                <div style={{ color: C.textDim, fontSize: 11, marginBottom: 8 }}>
                  Classify the customer's relationship to the identified PEP. Required for FATF R.12 compliance — all family members and close associates of PEPs must be subject to EDD.
                </div>
                <select
                  value={customer.pepRelationship || ""}
                  onChange={e => onSavePepRelationship(customer, e.target.value || null)}
                  style={{ width: "100%", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "8px 10px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none" }}
                >
                  <option value="">— Select relationship —</option>
                  <option value="direct_pep">Direct PEP (self)</option>
                  <option value="spouse">Spouse / Partner</option>
                  <option value="child">Child</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="close_associate">Close Associate / Business Partner</option>
                  <option value="beneficial_owner">Beneficial Owner linked to PEP</option>
                </select>
                {customer.pepRelationship && (
                  <div style={{ marginTop: 8, color: C.warn, fontSize: 11, fontWeight: 600 }}>
                    ★ EDD required — PEP-linked customer classified as <strong>{customer.pepRelationship.replace(/_/g, " ")}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Name Intelligence ─────────────────────────────────────────── */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600 }}>{t('kyc.nameIntelligence')}</div>
                {!customer.nameOrigin && (
                  <Btn small variant="subtle"
                    disabled={checkingNameOriginId === customer.id}
                    onClick={() => onRunNameOrigin(customer.id)}>
                    {checkingNameOriginId === customer.id ? t('kyc.drawer.checkingOrigin') : t('kyc.drawer.checkNameOrigin')}
                  </Btn>
                )}
                {customer.nameOrigin && (
                  <Btn small variant="ghost" onClick={() => runScreening(customer.id)} disabled={scanning || dilisenseRateLimited} title={dilisenseRateLimited ? `Available in ${dilisenseTimeUntilAvailable}s` : ''}>
                    {dilisenseRateLimited ? `⏳ ${dilisenseTimeUntilAvailable}s` : scanning ? "…" : t('kyc.screen')}
                  </Btn>
                )}
              </div>

              {!customer.nameOrigin && checkingNameOriginId !== customer.id && (
                <div style={{ padding: "14px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textDim, fontSize: 12 }}>
                  {t('kyc.drawer.checkNameOriginHint')}
                </div>
              )}

              {customer.nameOrigin && (() => {
                const { countryOrigin, countryOriginAlt, score, isFATFBlacklist, isFATFHighRisk, aiAssessment } = customer.nameOrigin;
                const originName = COUNTRY_NAMES[countryOrigin] || countryOrigin || "Unknown";
                const altName    = COUNTRY_NAMES[countryOriginAlt] || countryOriginAlt;
                const borderColor = isFATFBlacklist ? "rgba(255,68,68,0.35)" : isFATFHighRisk ? "rgba(255,140,0,0.35)" : "rgba(0,212,255,0.2)";
                const bgColor     = isFATFBlacklist ? "rgba(255,68,68,0.07)" : isFATFHighRisk ? "rgba(255,140,0,0.07)" : "rgba(0,212,255,0.04)";
                const statusColor = isFATFBlacklist ? C.danger : isFATFHighRisk ? C.warn : C.accent;
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {/* Origin card */}
                    <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "14px 16px" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: (isFATFBlacklist || isFATFHighRisk) ? 10 : 0 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{t('kyc.drawer.nameOrigin', { name: originName })}</div>
                          <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>
                            {altName && altName !== originName && t('kyc.drawer.nameAlt', { name: altName })}
                            {t('kyc.drawer.confidence', { pct: Math.round((score || 0) * 100) })}
                          </div>
                        </div>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, fontWeight: 700, color: statusColor, letterSpacing: "0.05em" }}>
                          {countryOrigin || "—"}
                        </span>
                      </div>
                      {isFATFBlacklist && (
                        <div style={{ background: "rgba(255,68,68,0.12)", border: `1px solid rgba(255,68,68,0.3)`, borderRadius: 6, padding: "10px 12px" }}>
                          <div style={{ color: C.danger, fontSize: 12, fontWeight: 700, marginBottom: 3 }}>{t('kyc.drawer.fatfBlacklistTitle')}</div>
                          <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>{t('kyc.drawer.fatfBlacklistBody')}</div>
                        </div>
                      )}
                      {!isFATFBlacklist && isFATFHighRisk && (
                        <div style={{ background: "rgba(255,140,0,0.1)", border: `1px solid rgba(255,140,0,0.3)`, borderRadius: 6, padding: "10px 12px" }}>
                          <div style={{ color: C.warn, fontSize: 12, fontWeight: 700, marginBottom: 3 }}>{t('kyc.drawer.fatfGreyTitle')}</div>
                          <div style={{ color: C.textMid, fontSize: 11, lineHeight: 1.6 }}>{t('kyc.drawer.fatfGreyBody')}</div>
                        </div>
                      )}
                      {!isFATFBlacklist && !isFATFHighRisk && (
                        <div style={{ color: C.textDim, fontSize: 11 }}>{t('kyc.drawer.notFatf')}</div>
                      )}
                    </div>

                    {/* AI risk synthesis */}
                    {aiAssessment && (
                      <div style={{ background: "rgba(138,43,226,0.06)", border: "1px solid rgba(138,43,226,0.25)", borderRadius: 8, padding: "12px 14px" }}>
                        <div style={{ color: C.purple || "#A78BFA", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>{t('kyc.drawer.aiRiskTitle')}</div>
                        <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7 }}>{aiAssessment}</div>
                      </div>
                    )}
                    {!aiAssessment && customer.matchData && (
                      <div style={{ color: C.textDim, fontSize: 11, fontStyle: "italic" }}>{t('kyc.drawer.runScreeningFirst')}</div>
                    )}
                  </div>
                );
              })()}
            </div>

          {/* Narrative / Analysis Notes */}
          <div>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 10 }}>SCREENING NARRATIVE</div>
            <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
              <textarea
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                placeholder="Add analysis notes, observations, or compliance narrative here..."
                rows={4}
                style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, padding: "10px 12px", fontFamily: "'DM Mono', monospace", fontSize: 12, outline: "none", resize: "vertical", boxSizing: "border-box" }}
              />
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ color: C.textDim, fontSize: 11 }}>{narrative.length} characters</div>
                <Btn small onClick={saveNarrative} variant={narrativeSaved ? "success" : "primary"}>
                  {narrativeSaved ? "✓ Saved" : "Save Notes"}
                </Btn>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── MAIN KYC PAGE ────────────────────────────────────────────────────────────
export default function SanctionsScreeningPage({ currentUser, company }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const [customers, setCustomers] = useState([]);

  const [showUpload, setShowUpload]     = useState(false);
  const [showNew, setShowNew]           = useState(false);
  const [searchTerm, setSearchTerm]     = useState("");
  const [filterRisk, setFilterRisk]     = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [scanningId, setScanningId]     = useState(null);
  const [checkingNameOriginId, setCheckingNameOriginId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [bulkScanProgress, setBulkScanProgress] = useState(null);
  const [newForm, setNewForm]           = useState({ name: "", type: "Individual", country: "", riskLevel: "Medium", notes: "" });
  const [dragOver, setDragOver]         = useState(false);
  const [screeningError, setScreeningError] = useState("");
  const [narrative, setNarrative]       = useState("");
  const [narrativeSaved, setNarrativeSaved] = useState(false);
  const [dilisenseRateLimited, setDilisenseRateLimited] = useState(false);
  const [dilisenseTimeUntilAvailable, setDilisenseTimeUntilAvailable] = useState(0);
  const fileInputRef = useRef(null);
  const hasAutoScreened = useRef(false); // F-004: prevent repeated auto-trigger
  const rateCheckInterval = useRef(null);

  // Save narrative to localStorage
  const saveNarrative = () => {
    if (!selectedCustomer) return;
    const key = `sanctions_narrative_${selectedCustomer.name.toLowerCase().replace(/\s+/g, '_')}`;
    localStorage.setItem(key, narrative);
    setNarrativeSaved(true);
    setTimeout(() => setNarrativeSaved(false), 2000);
  };

  // Load narrative from localStorage when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      const key = `sanctions_narrative_${selectedCustomer.name.toLowerCase().replace(/\s+/g, '_')}`;
      const saved = localStorage.getItem(key) || "";
      setNarrative(saved);
    }
  }, [selectedCustomer?.id]);

  // Monitor Dilisense rate limit status and update UI
  useEffect(() => {
    const updateRateLimitStatus = () => {
      loadDilisenseRateLimitState();
      const timeUntil = getDilisenseTimeUntilNextRequest();
      const isLimited = timeUntil > 0;
      setDilisenseRateLimited(isLimited);
      setDilisenseTimeUntilAvailable(Math.ceil(timeUntil / 1000));
    };

    updateRateLimitStatus();

    // Check rate limit status every second
    if (rateCheckInterval.current) clearInterval(rateCheckInterval.current);
    rateCheckInterval.current = setInterval(updateRateLimitStatus, 1000);

    return () => {
      if (rateCheckInterval.current) clearInterval(rateCheckInterval.current);
    };
  }, []);

  // ── Load customers from Neon sanctions_screening table ─────────────────
  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      try {
        const result = await db.from("sanctions_screening")
          .select("*")
          .eq("company_id", company.id)
          .order("created_at", { ascending: false })
          .execute();
        const data = result.data || [];
        if (!data?.length) {
          setCustomers([]);
          return;
        }
        // Build customer list from unique subject_names, merging latest result per name
        const byName = {};
        data.forEach(row => {
          const key = row.subject_name;
          if (!byName[key]) {
            // Extract match details - prioritize matches field which has pep/sanctions/criminal structure
            let matchDetail = row.matches || row.raw_response || row.match_detail || {};
            if (typeof matchDetail === 'string') {
              try {
                matchDetail = JSON.parse(matchDetail);
              } catch (e) {
                matchDetail = {};
              }
            }
            const sanctions = matchDetail.sanctions || [];
            const pep = matchDetail.pep || [];
            const criminal = matchDetail.criminal || [];

            // Debug: Log what we're getting from the database
            if (key === "Donald Trump") {
              console.log("[loadScreenings] Donald Trump data from DB:", {
                matchDetail: matchDetail,
                matchDetailType: typeof matchDetail,
                sanctionsLength: sanctions.length,
                pepLength: pep.length,
                criminalLength: criminal.length,
                rawMatchDetail: row.match_detail
              });
            }

            // Set status for each check type based on actual matches
            const sanctionsStatus = sanctions.length > 0 ? "failed" : "passed";
            const watchlistStatus = pep.length > 0 ? "failed" : "passed";
            const criminalStatus = criminal.length > 0 ? "failed" : "passed";

            byName[key] = {
              id:           row.id,
              name:         row.subject_name,
              type:         row.screen_type === "business" ? "Business" : "Individual",
              country:      row.subject_nationality || "",
              riskLevel:    row.risk_score >= 70 ? "High" : row.risk_score >= 40 ? "Medium" : "Low",
              sanctions:    sanctionsStatus,
              watchlist:    watchlistStatus,
              criminal:     criminalStatus,
              dateAdded:    row.created_at?.slice(0, 10) || new Date().toISOString().slice(0, 10),
              lastScreened: row.screened_at,
              totalHits:    row.match_count || 0,
              notes:        row.notes || "",
              // Restore full match detail
              matchData: { sanctions, watchlist: pep, criminal },
              reviewStatus:      row.review_status || null,
              reviewedAt:        row.reviewed_at || null,
              pepRelationship:   row.pep_relationship_type || null,
            };
          }
        });
        const list = Object.values(byName);
        setCustomers(list);

        // E-011 / F-004: Create notifications for overdue KYC refreshes
        if (company?.id) {
          const overdue = list.filter(isKYCRefreshOverdue);
          for (const c of overdue) {
            const days = c.riskLevel === "High" ? 180 : 365;
            try {
              await db.from("notifications").insert({
                company_id: company.id,
                type: "kyc_refresh_overdue",
                title: `KYC Refresh Overdue — ${c.name}`,
                message: `${c.riskLevel === "High" ? "High-risk" : "Standard"} customer ${c.name} has not been re-screened in ${days} days. Re-screening is required.`,
                severity: c.riskLevel === "High" ? "high" : "medium",
                entity_id: String(c.id),
                entity_name: c.name,
                is_read: false,
              }).execute();
            } catch (notifErr) {
              console.warn("[Sanctions Screening] Notification insert failed (may be duplicate):", notifErr.message?.substring(0, 100));
            }
          }
        }
      } catch (err) {
        console.warn("[Sanctions Screening] Load error (table may not exist yet):", err.message?.substring(0, 100));
        setCustomers([]);
      }
    })();
  }, [company?.id, currentUser?.id]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = {
    total:   customers.length,
    cleared: customers.filter(c => c.sanctions === "passed" && c.watchlist === "passed" && c.criminal === "passed").length,
    flagged: customers.filter(c => c.sanctions === "failed" || c.watchlist === "failed" || c.criminal === "failed").length,
    pending: customers.filter(c => c.sanctions === "pending" || c.watchlist === "pending" || c.criminal === "pending").length,
    overdue: customers.filter(isKYCRefreshOverdue).length,
  };

  // F-004: Auto-trigger re-screening once per session when overdue customers are found
  // DISABLED: Auto-screening was competing with manual screening and causing rate limit issues
  // Users can manually trigger re-screening using the "Re-screen Overdue" button
  // useEffect(() => {
  //   if (!hasAutoScreened.current && customers.length > 0 && !bulkScanProgress) {
  //     const overdue = customers.filter(isKYCRefreshOverdue);
  //     if (overdue.length > 0) {
  //       hasAutoScreened.current = true;
  //       runRescreenOverdue(); // eslint-disable-line react-hooks/exhaustive-deps
  //     }
  //   }
  // }, [customers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = customers.filter(c => {
    if (searchTerm && !c.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterRisk !== "All" && c.riskLevel !== filterRisk) return false;
    if (filterStatus === "Cleared" && !(c.sanctions === "passed" && c.watchlist === "passed" && c.criminal === "passed")) return false;
    if (filterStatus === "Flagged" && !(c.sanctions === "failed" || c.watchlist === "failed" || c.criminal === "failed")) return false;
    if (filterStatus === "Pending" && !(c.sanctions === "pending" || c.watchlist === "pending" || c.criminal === "pending")) return false;
    return true;
  });

  // ── Real Dilisense screening ────────────────────────────────────────────────
  async function runScreening(customerId) {
    setScreeningError("");
    setScanningId(customerId);
    const customer = customers.find(c => c.id === customerId);

    // Reset to scanning state
    setCustomers(prev => prev.map(c =>
      c.id === customerId ? { ...c, sanctions: "pending", watchlist: "pending", criminal: "pending", matchData: {} } : c
    ));
    if (selectedCustomer?.id === customerId) {
      setSelectedCustomer(prev => ({ ...prev, sanctions: "pending", watchlist: "pending", criminal: "pending", matchData: {} }));
    }

    try {
      const data = await dilisenseCheck(customer.name, customer.type);
      const results = parseResults(data);

      // Debug logging
      console.log("[Sanctions Screening] Dilisense raw data:", data);
      console.log("[Sanctions Screening] Parsed results:", {
        totalHits: results.totalHits,
        sanctionsCount: (results.sanctions || []).length,
        pepCount: (results.pep || []).length,
        criminalCount: (results.criminal || []).length,
        sanctionsStatus: results.sanctionsStatus,
        watchlistStatus: results.watchlistStatus,
        criminalStatus: results.criminalStatus
      });

      const updated = {
        sanctions:    results.sanctionsStatus,
        watchlist:    results.watchlistStatus,
        criminal:     results.criminalStatus,
        totalHits:    results.totalHits,
        lastScreened: new Date().toISOString(),
        matchData: {
          sanctions: results.sanctions,
          watchlist: results.pep,
          criminal:  results.criminal,
        },
        // Reset review status on re-screen — new screening result requires fresh review
        reviewStatus: null,
        reviewedAt:   null,
      };

      // ── NamSor name intelligence (non-blocking, runs in parallel) ──────────
      try {
        const nameOrigin = await namsorCheck(customer.name);
        updated.nameOrigin = nameOrigin;
        // Auto-elevate to High risk if FATF blacklist
        if (nameOrigin.isFATFBlacklist && customer.riskLevel !== "High") {
          updated.riskLevel = "High";
        }
        // AI synthesis — combines sanctions hits + name origin into a single assessment
        try {
          const aiAssessment = await aiRiskSynthesis(customer.name, updated.matchData, nameOrigin);
          updated.nameOrigin = { ...nameOrigin, aiAssessment };
        } catch {
          // AI synthesis failure never blocks the result
        }
      } catch {
        // NamSor failure never blocks the main Dilisense result
      }

      setCustomers(prev => prev.map(c => c.id === customerId ? { ...c, ...updated } : c));
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(prev => ({ ...prev, ...updated }));
      }

      // ── Write result to Neon sanctions_screening table ──────────────────
      if (company?.id) {
        const overallResult = results.totalHits === 0 ? "clear"
          : results.sanctions.length > 0 ? "confirmed_match"
          : "potential_match";

        try {
          // Simplify matches to just essential fields to avoid JSON serialization issues
          const simpleMatches = {
            sanctions: (results.sanctions || []).map(m => ({ name: m.name || m.entity_name, country: m.country, score: m.score })),
            pep: (results.pep || []).map(m => ({ name: m.name || m.entity_name, country: m.country, score: m.score })),
            criminal: (results.criminal || []).map(m => ({ name: m.name || m.entity_name, country: m.country, score: m.score }))
          };

          const matchDetail = { sanctions: results.sanctions || [], pep: results.pep || [], criminal: results.criminal || [] };
          console.log("[Sanctions Screening] About to save match_detail:", matchDetail);

          // Log what we're sending to the database
          const insertPayload = {
            company_id:          company.id,
            screened_by:         currentUser?.id || null,
            screened_at:         new Date().toISOString(),
            screen_type:         customer.type === "Business" ? "business" : "individual",
            subject_name:        customer.name,
            subject_nationality: customer.country || null,
            lists_checked:       [
              "OFAC SDN", "OFAC SSI", "OFAC ISN",
              "UN Consolidated List",
              "EU AML/CFT Consolidated List",
              "UK HMT Consolidated List",
              "Australian DFAT List",
              "Canadian DFAT List",
              "Swiss SECO List",
              "FATF High-Risk Countries",
              "Interpol Red Notices",
              "Global Criminal Records",
              "PEP Databases (Global)",
              "120+ Additional Regulatory Sources"
            ],
            result:              overallResult,
            match_count:         results.totalHits,
            // Store full match details in matches field (has pep/sanctions/criminal structure)
            // raw_response is kept for backward compatibility
            raw_response:        matchDetail,
            matches:             Object.values(matchDetail).some(arr => arr.length > 0) ? matchDetail : null,
            risk_score:          results.totalHits === 0 ? 0 : results.sanctions.length > 0 ? 90 : results.pep.length > 0 ? 80 : 50,
            provider:            "Dilisense",
            notes:               customer.notes || null,
          };

          console.log("[Sanctions Screening] Payload being inserted:", {
            ...insertPayload,
            match_detail: matchDetail,
            pep_array: results.pep,
            pep_length: (results.pep || []).length
          });

          // Use raw SQL endpoint to bypass ORM JSONB serialization issues
          const insertResult = await fetch('/api/insert-screening', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + (currentUser?.id || '')
            },
            body: JSON.stringify(insertPayload)
          }).then(r => r.json());

          if (insertResult.error) {
            throw new Error(insertResult.message || 'Failed to insert screening');
          }

          console.log("[Sanctions Screening] Result saved to database:", insertResult);
          console.log("[Sanctions Screening] Full returned data:", JSON.stringify(insertResult.data, null, 2));

          // Update local state with the newly inserted record
          if (insertResult.data) {
            const newRecord = insertResult.data;
            // Data is stored in 'matches' column, not 'match_detail'
            let matchData = newRecord.matches || newRecord.match_detail || {};
            if (typeof matchData === 'string') {
              matchData = JSON.parse(matchData);
            }

            const sanctions = matchData.sanctions || [];
            const pep = matchData.pep || [];
            const criminal = matchData.criminal || [];

            console.log("[Sanctions Screening] Extracted from matches: sanctions:", sanctions.length, "pep:", pep.length, "criminal:", criminal.length);

            const newCustomer = {
              id:           newRecord.id,
              name:         newRecord.subject_name,
              type:         newRecord.screen_type === "business" ? "Business" : "Individual",
              country:      newRecord.subject_nationality || "",
              riskLevel:    newRecord.risk_score >= 70 ? "High" : newRecord.risk_score >= 40 ? "Medium" : "Low",
              sanctions:    sanctions.length > 0 ? "failed" : "passed",
              watchlist:    pep.length > 0 ? "failed" : "passed",
              criminal:     criminal.length > 0 ? "failed" : "passed",
              dateAdded:    newRecord.created_at?.slice(0, 10) || new Date().toISOString().slice(0, 10),
              lastScreened: newRecord.screened_at,
              totalHits:    newRecord.match_count || 0,
              notes:        newRecord.notes || "",
              matchData:    { sanctions, watchlist: pep, criminal },
              reviewStatus: null,
              reviewedAt:   null,
            };

            console.log("[Sanctions Screening] Updating customer with:", { name: newCustomer.name, watchlist: newCustomer.watchlist, sanctions: newCustomer.sanctions, criminal: newCustomer.criminal });

            // Update customers list - replace old record or add new one
            setCustomers(prev => {
              const filtered = prev.filter(c => c.name !== customer.name);
              return [newCustomer, ...filtered];
            });

            if (selectedCustomer?.name === customer.name) {
              // Use full newRecord data for detail panel to have all screening fields
              setSelectedCustomer({
                ...newRecord,
                name: newRecord.subject_name,
                watchlist: pep.length > 0 ? "failed" : "passed",
                sanctions: sanctions.length > 0 ? "failed" : "passed",
                criminal: criminal.length > 0 ? "failed" : "passed"
              });
            }
          }

          // Note: Customer data is loaded from sanctions_screening table
          // The customer.id here is the sanctions_screening record ID, not a customer record ID
          // Screening results are persisted in sanctions_screening table via the INSERT above
          // No need to separately update customers table at this time
        } catch (dbErr) {
          console.error("[Sanctions Screening] Failed to save result to database:", dbErr);
          // Don't fail the screening if DB save fails — user can still see results
        }
        // F-005: Detect new hits vs prior screening — send notification if a new hit appeared
        const priorHits = customer.totalHits || 0;
        const priorReviewStatus = customer.reviewStatus;
        if (results.totalHits > 0 && (priorHits === 0 || (results.totalHits > priorHits && priorReviewStatus !== "confirmed" && priorReviewStatus !== "false_positive"))) {
          const hitTypes = [
            results.sanctions.length > 0  ? "Sanctions" : null,
            results.pep.length > 0        ? "PEP/Watchlist" : null,
            results.criminal.length > 0   ? "Criminal" : null,
          ].filter(Boolean).join(", ");
          await db.from("notifications").insert({
            company_id:  company.id,
            type:        "sanctions_rescreening_hit",
            title:       `Re-screening Hit — ${customer.name}`,
            message:     `Re-screening of ${customer.name} returned ${results.totalHits} match${results.totalHits !== 1 ? "es" : ""} (${hitTypes}). Please review the screening results and confirm or dismiss the alert.`,
            severity:    results.sanctions.length > 0 ? "high" : "medium",
            entity_id:   String(customer.id),
            entity_name: customer.name,
            is_read:     false,
          });
        }

        await log({
          action: "sanctions_screening_completed",
          module: "kyc",
          entityName: customer.name,
          meta: {
            result: overallResult,
            total_hits: results.totalHits,
            sanctions_hits: results.sanctions.length,
            pep_hits: results.pep.length,
            criminal_hits: results.criminal.length,
            lists_checked: "OFAC (SDN, SSI, ISN), UN, EU, UK HMT, DFAT (AU/CA), Swiss SECO, FATF, Interpol, PEP Global, 120+ Additional Sources",
            provider: "Dilisense",
          },
        });
      }
    } catch (err) {
      let errorMsg = `Screening failed: ${err.message}`;

      // Provide user-friendly error messages for specific scenarios
      if (err.message.includes("429") || err.message.includes("Rate limited")) {
        errorMsg = "⏳ Rate limit reached. Please wait a moment and try again. The system will automatically retry.";
      } else if (err.message.includes("not configured")) {
        errorMsg = "⚠️ Dilisense API key not configured. Contact your administrator.";
      } else if (err.message.includes("Bad request")) {
        errorMsg = "❌ Invalid customer name. Please check the spelling.";
      } else if (err.message.includes("temporarily unavailable")) {
        errorMsg = "🔄 Screening service temporarily unavailable. Please try again shortly.";
      }

      setScreeningError(errorMsg);
      console.error("[Sanctions Screening] Error:", err);

      // Revert to pending
      setCustomers(prev => prev.map(c =>
        c.id === customerId ? { ...c, sanctions: "pending", watchlist: "pending", criminal: "pending" } : c
      ));
    } finally {
      setScanningId(null);
    }
  }

  // ── Standalone NamSor name origin check ────────────────────────────────────
  async function runNameOrigin(customerId) {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    setCheckingNameOriginId(customerId);
    try {
      let nameOrigin = await namsorCheck(customer.name);
      // AI synthesis — use existing matchData if customer has already been screened
      if (customer.matchData) {
        try {
          const aiAssessment = await aiRiskSynthesis(customer.name, customer.matchData, nameOrigin);
          nameOrigin = { ...nameOrigin, aiAssessment };
        } catch {
          // AI synthesis failure never blocks the result
        }
      }
      const riskElevated = nameOrigin.isFATFBlacklist && customer.riskLevel !== "High";
      const patch = { nameOrigin, ...(riskElevated ? { riskLevel: "High" } : {}) };
      setCustomers(prev => prev.map(c => c.id === customerId ? { ...c, ...patch } : c));
      if (selectedCustomer?.id === customerId) {
        setSelectedCustomer(prev => ({ ...prev, ...patch }));
      }
    } catch (err) {
      setScreeningError(`Name origin check failed: ${err.message}`);
    } finally {
      setCheckingNameOriginId(null);
    }
  }

  // ── Bulk screening ─────────────────────────────────────────────────────────
  async function runBulkScreening() {
    const targets = customers.filter(c => c.sanctions === "pending" || c.watchlist === "pending" || c.criminal === "pending");
    if (!targets.length) return;
    setBulkScanProgress({ current: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      const customer = targets[i];
      setBulkScanProgress({ current: i + 1, total: targets.length });
      await runScreening(customer.id);
      // Small delay to avoid rate-limiting
      if (i < targets.length - 1) await new Promise(r => setTimeout(r, 500));
    }
    setBulkScanProgress(null);
  }

  // F-005: Batch re-screen all KYC-overdue customers
  async function runRescreenOverdue() {
    const targets = customers.filter(isKYCRefreshOverdue);
    if (!targets.length) return;
    setBulkScanProgress({ current: 0, total: targets.length, label: "Re-screening overdue" });
    for (let i = 0; i < targets.length; i++) {
      setBulkScanProgress({ current: i + 1, total: targets.length, label: "Re-screening overdue" });
      await runScreening(targets[i].id);
      if (i < targets.length - 1) await new Promise(r => setTimeout(r, 500));
    }
    setBulkScanProgress(null);
  }

  // F-005: Mark a screening result as confirmed hit, false positive, or reset (null)
  async function reviewHit(customer, status) {
    try {
      const patch = status
        ? { reviewStatus: status, reviewedAt: new Date().toISOString() }
        : { reviewStatus: null, reviewedAt: null };

      // Update UI immediately
      setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, ...patch } : c));
      if (selectedCustomer?.id === customer.id) setSelectedCustomer(prev => ({ ...prev, ...patch }));

      if (company?.id) {
        // Save to database
        const { error } = await db.from("sanctions_screening")
          .update({
            review_status: status || null,
            reviewed_by: status ? (currentUser?.id || null) : null
          })
          .eq("id", customer.id)
          .eq("company_id", company.id)
          .execute();

        if (error) {
          console.error("[Sanctions Screening] Failed to save review status:", error);
          setScreeningError(`Failed to save review status: ${error.message}`);
          // Revert UI on failure
          setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, reviewStatus: customer.reviewStatus, reviewedAt: customer.reviewedAt } : c));
          if (selectedCustomer?.id === customer.id) setSelectedCustomer(prev => ({ ...prev, reviewStatus: customer.reviewStatus, reviewedAt: customer.reviewedAt }));
          return;
        }

        if (status) {
          await log({
            action: status === "confirmed" ? "screening_hit_confirmed" : "screening_false_positive",
            module: "kyc",
            entityName: customer.name,
            meta: { review_status: status, screening_id: customer.id },
          });

          // Dismiss the re-screening notification for this customer when reviewed
          await db.from("notifications")
            .update({ is_read: true })
            .eq("company_id", company.id)
            .eq("type", "sanctions_rescreening_hit")
            .eq("entity_id", String(customer.id))
            .execute();
        }
      }
    } catch (err) {
      console.error("[Sanctions Screening] Error in reviewHit:", err);
      setScreeningError(`Error saving review status: ${err.message}`);
    }
  }

  // F-006: Save PEP relationship type
  async function savePepRelationship(customer, relationship) {
    const patch = { pepRelationship: relationship || null };
    setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, ...patch } : c));
    if (selectedCustomer?.id === customer.id) setSelectedCustomer(prev => ({ ...prev, ...patch }));
    if (company?.id) {
      await db.from("sanctions_screening")
        .update({ pep_relationship_type: relationship || null })
        .eq("id", customer.id).execute();
      if (relationship) {
        await log({
          action: "pep_relationship_set",
          module: "kyc",
          entityName: customer.name,
          meta: { pep_relationship_type: relationship },
        });
      }
    }
  }

  async function saveCustomer(updated) {
    setCustomers(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
    const idStr = String(updated.id ?? "");
    if (company?.id && !idStr.startsWith("mock-") && updated.notes !== undefined) {
      await db.from("sanctions_screening")
        .update({ notes: updated.notes })
        .eq("id", updated.id).execute();
    }
    setSelectedCustomer(prev => ({ ...prev, ...updated }));
  }

  function deleteCustomer(id) {
    setCustomers(prev => prev.filter(c => c.id !== id));
    if (selectedCustomer?.id === id) setSelectedCustomer(null);
  }

  async function addCustomer() {
    if (!newForm.name.trim()) return;
    const customer = {
      id: Date.now(), ...newForm,
      sanctions: "pending", watchlist: "pending", criminal: "pending",
      dateAdded: new Date().toISOString().slice(0, 10), matchData: {},
    };
    setCustomers(prev => [...prev, customer]);
    setNewForm({ name: "", type: "Individual", country: "", riskLevel: "Medium", notes: "" });
    setShowNew(false);

    // Write a placeholder screening record so customer persists across reloads
    if (company?.id) {
      await db.from("sanctions_screening").insert({
        company_id:         company.id,
        screened_by:        currentUser?.id || null,
        screen_type:        newForm.type === "Business" ? "business" : "individual",
        subject_name:       newForm.name.trim(),
        subject_nationality: newForm.country || null,
        lists_checked:      ["OFAC SDN", "UN", "EU", "HMT"],
        result:             "clear",
        match_count:        0,
        risk_score:         newForm.riskLevel === "High" ? 70 : newForm.riskLevel === "Medium" ? 40 : 10,
        provider:           "pending",
      });
    }
  }

  // ── CSV Import ─────────────────────────────────────────────────────────────
  function processCSV(text) {
    const lines = text.split("\n").filter(l => l.trim());
    if (lines.length < 2) { alert("CSV must have a header row and at least one data row."); return; }
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const get = (vals, keys) => { for (const k of keys) { const idx = headers.indexOf(k); if (idx > -1) return vals[idx]?.trim().replace(/^"|"$/g, "") || ""; } return ""; };
    const newCustomers = lines.slice(1).filter(l => l.trim()).map((line, idx) => {
      const vals = line.split(",");
      const riskRaw = get(vals, ["risk", "risklevel", "risk_level"]);
      const riskLevel = ["Low","Medium","High"].includes(riskRaw) ? riskRaw : riskRaw ? riskRaw.charAt(0).toUpperCase() + riskRaw.slice(1).toLowerCase() : "Medium";
      return {
        id: Date.now() + idx,
        name: get(vals, ["name"]) || "Unknown",
        type: get(vals, ["type"]) || "Individual",
        country: get(vals, ["country"]) || "",
        riskLevel: ["Low","Medium","High"].includes(riskLevel) ? riskLevel : "Medium",
        sanctions: "pending", watchlist: "pending", criminal: "pending",
        dateAdded: new Date().toISOString().slice(0, 10),
        notes: get(vals, ["notes"]) || "",
        matchData: {},
      };
    });
    setCustomers(prev => [...prev, ...newCustomers]);
    alert(`✓ Imported ${newCustomers.length} customer${newCustomers.length !== 1 ? "s" : ""}`);
    setShowUpload(false);
  }

  function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => processCSV(ev.target.result);
    reader.readAsText(file);
    e.target.value = "";
  }

  function handleDrop(e) {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => processCSV(ev.target.result);
    reader.readAsText(file);
  }

  function downloadTemplate() {
    const csv = ["name,type,country,risk,notes", "John Doe,Individual,US,Low,Initial screening", "Acme Corp,Business,UK,High,High volume transactions"].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "kyc_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  function getOverallStatus(c) {
    if (c.sanctions === "failed" || c.watchlist === "failed" || c.criminal === "failed") return "failed";
    if (c.sanctions === "passed" && c.watchlist === "passed" && c.criminal === "passed") return "passed";
    return "pending";
  }

  const pendingCount = customers.filter(c => c.sanctions === "pending" || c.watchlist === "pending" || c.criminal === "pending").length;

  return (
    <div style={{ fontFamily: "'DM Mono', monospace" }}>
      <style>{`
        @keyframes kycPulse { 0%,100%{opacity:0.4;transform:scale(0.85)} 50%{opacity:1;transform:scale(1.1)} }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>{t('kyc.title')}</h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>{t('kyc.subtitle', { count: customers.length })}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {stats.overdue > 0 && (
            <Btn variant="warn" onClick={runRescreenOverdue} disabled={!!bulkScanProgress}>
              {bulkScanProgress?.label === "Re-screening overdue"
                ? `Re-screening ${bulkScanProgress.current}/${bulkScanProgress.total}…`
                : `⏰ Re-screen Overdue (${stats.overdue})`}
            </Btn>
          )}
          {pendingCount > 0 && (
            <Btn variant="warn" onClick={runBulkScreening} disabled={!!bulkScanProgress}>
              {bulkScanProgress ? t('kyc.bulkScreening', { current: bulkScanProgress.current, total: bulkScanProgress.total }) : t('kyc.bulkScreenAll', { count: pendingCount })}
            </Btn>
          )}
          <Btn variant="ghost" onClick={() => setShowUpload(true)}>{t('kyc.importCSV')}</Btn>
          <Btn onClick={() => setShowNew(true)}>+ {t('kyc.addCustomer')}</Btn>
          <Btn variant="danger" onClick={() => { if (window.confirm(t('kyc.clearAllConfirm'))) setCustomers([]); }}>{t('kyc.clearAll')}</Btn>
        </div>
      </div>

      {/* Error banner */}
      {screeningError && (
        <div style={{ padding: "10px 16px", background: "rgba(255,68,68,0.1)", border: `1px solid rgba(255,68,68,0.3)`, borderRadius: 8, color: C.danger, fontSize: 13, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {screeningError}
          <button onClick={() => setScreeningError("")} style={{ background: "transparent", border: "none", color: C.danger, cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>
      )}

      {/* KYC refresh overdue banner (E-011 / F-004) */}
      {stats.overdue > 0 && (
        <div style={{ background: "rgba(255,140,0,0.08)", border: "1px solid rgba(255,140,0,0.35)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ color: C.warn, fontSize: 18 }}>⏰</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.warn, fontWeight: 700, fontSize: 13 }}>KYC Periodic Refresh Overdue — {stats.overdue} customer{stats.overdue !== 1 ? "s" : ""}</div>
            <div style={{ color: C.textMid, fontSize: 12, marginTop: 2 }}>High-risk customers must be re-screened every 6 months; standard customers every 12 months. Run screening for flagged customers below.</div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: t('kyc.stats.total'),   value: stats.total,   color: C.accent,   icon: "◈" },
          { label: t('kyc.stats.cleared'), value: stats.cleared, color: C.success,  icon: "✓" },
          { label: t('kyc.stats.flagged'), value: stats.flagged, color: C.danger,   icon: "⚠" },
          { label: t('kyc.stats.pending'), value: stats.pending, color: C.warn,     icon: "⏳" },
          { label: "REFRESH DUE",          value: stats.overdue, color: stats.overdue > 0 ? C.warn : C.textDim, icon: "⏰" },
        ].map(s => (
          <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `2px solid ${s.color}`, borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: s.color, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700 }}>{s.icon} {s.value}</div>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap", alignItems: "center" }}>
        <input type="text" placeholder={t('kyc.searchPlaceholder')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: 200, maxWidth: 320, padding: "9px 14px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none" }} />
        {[
          { label: t('kyc.filterRisk'),   value: filterRisk,   set: setFilterRisk,   opts: [{ val: "All", label: t('kyc.filterAllRisks') }, { val: "Low", label: t('kyc.riskLevels.low') }, { val: "Medium", label: t('kyc.riskLevels.medium') }, { val: "High", label: t('kyc.riskLevels.high') }] },
          { label: t('kyc.filterStatus'), value: filterStatus, set: setFilterStatus, opts: [{ val: "All", label: t('kyc.filterAllStatuses') }, { val: "Cleared", label: t('kyc.stats.cleared') }, { val: "Flagged", label: t('kyc.stats.flagged') }, { val: "Pending", label: t('kyc.stats.pending') }] },
        ].map(f => (
          <select key={f.label} value={f.value} onChange={e => f.set(e.target.value)}
            style={{ padding: "9px 14px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none" }}>
            {f.opts.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
        ))}
        <span style={{ color: C.textDim, fontSize: 12, marginLeft: "auto" }}>{filtered.length} of {customers.length}</span>
      </div>

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                {[
                  { key: "customer", label: t('kyc.headers.customer') },
                  { key: "type",     label: t('kyc.headers.type') },
                  { key: "country",  label: t('kyc.headers.country') },
                  { key: "risk",     label: t('kyc.headers.risk') },
                  { key: "sanctions",label: t('kyc.headers.sanctions') },
                  { key: "pep",      label: t('kyc.headers.pep') },
                  { key: "criminal", label: t('kyc.headers.criminal') },
                  { key: "overall",  label: t('kyc.headers.overall') },
                  { key: "actions",  label: t('kyc.headers.actions') },
                ].map(h => (
                  <th key={h.key} style={{ padding: "11px 16px", textAlign: h.key === "actions" ? "center" : "left", color: C.textDim, fontWeight: 600, fontSize: 10, letterSpacing: "0.08em" }}>{h.label.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="9" style={{ padding: "52px", textAlign: "center", color: C.textDim }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>◈</div>{t('kyc.noCustomersFilter')}
                </td></tr>
              ) : filtered.map((customer, idx) => {
                const isScanning = scanningId === customer.id;
                const overall = getOverallStatus(customer);
                return (
                  <tr key={customer.id} style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.12)", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={e => e.currentTarget.style.background = C.surfaceAlt}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "transparent" : "rgba(0,0,0,0.12)"}
                    onClick={() => setSelectedCustomer(customer)}>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ color: C.text, fontWeight: 600 }}>{customer.name}</div>
                      {customer.lastScreened && <div style={{ color: C.textDim, fontSize: 10, marginTop: 2 }}>{t('kyc.screened', { date: new Date(customer.lastScreened).toLocaleDateString() })}</div>}
                      {isKYCRefreshOverdue(customer) && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "rgba(255,140,0,0.12)", color: C.warn, border: "1px solid rgba(255,140,0,0.35)", fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>
                            ⏰ REFRESH DUE
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", color: C.textMid }}>{customer.type}</td>
                    <td style={{ padding: "12px 16px", color: C.textMid }}>{customer.country || "—"}</td>
                    <td style={{ padding: "12px 16px" }}><RiskBadge level={customer.riskLevel} /></td>
                    {["sanctions", "watchlist", "criminal"].map(key => {
                      const val = customer[key];
                      const bt = isScanning ? "scanning" : (val === "passed" ? "passed" : val === "failed" ? "failed" : "pending");
                      return <td key={key} style={{ padding: "12px 16px" }}><Badge text={isScanning ? "…" : val} type={bt} /></td>;
                    })}
                    <td style={{ padding: "12px 16px" }}>
                      <Badge text={isScanning ? "…" : (overall === "passed" ? t('kyc.overall.clear') : overall === "failed" ? t('kyc.overall.flagged') : t('kyc.overall.pending'))} type={isScanning ? "scanning" : overall} />
                    </td>
                    <td style={{ padding: "12px 16px" }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <Btn small variant="subtle" onClick={() => setSelectedCustomer(customer)}>{t('kyc.view')}</Btn>
                        <Btn small variant="ghost" onClick={() => runScreening(customer.id)} disabled={isScanning || dilisenseRateLimited} title={dilisenseRateLimited ? `Available in ${dilisenseTimeUntilAvailable}s` : ''}>
                    {dilisenseRateLimited ? `⏳ ${dilisenseTimeUntilAvailable}s` : isScanning ? "…" : t('kyc.screen')}
                  </Btn>
                        <Btn small variant="danger" onClick={() => deleteCustomer(customer.id)}>✕</Btn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      {selectedCustomer && (
        <CustomerDrawer customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} onSave={saveCustomer}
          onRunScreening={runScreening} onRunNameOrigin={runNameOrigin} onReviewHit={reviewHit}
          onSavePepRelationship={savePepRelationship}
          scanningId={scanningId} checkingNameOriginId={checkingNameOriginId}
          narrative={narrative} setNarrative={setNarrative} saveNarrative={saveNarrative} narrativeSaved={narrativeSaved}
          dilisenseRateLimited={dilisenseRateLimited} dilisenseTimeUntilAvailable={dilisenseTimeUntilAvailable} />
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowUpload(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, width: 520 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{t('kyc.upload.title')}</div>
              <button onClick={() => setShowUpload(false)} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 22 }}>✕</button>
            </div>
            <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
              style={{ background: dragOver ? C.accentGlow : C.bg, border: `2px dashed ${dragOver ? C.accent : C.border}`, borderRadius: 10, padding: "40px 20px", textAlign: "center", marginBottom: 20, transition: "all 0.15s", cursor: "pointer" }}
              onClick={() => fileInputRef.current?.click()}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📄</div>
              <div style={{ color: C.text, fontWeight: 600, marginBottom: 4 }}>{t('kyc.upload.dropHint')}</div>
              <div style={{ color: C.textDim, fontSize: 12 }}>{t('kyc.upload.columns')}</div>
              <input type="file" accept=".csv" onChange={handleFileUpload} ref={fileInputRef} style={{ display: "none" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="ghost" onClick={downloadTemplate} style={{ flex: 1 }}>{t('kyc.upload.downloadTemplate')}</Btn>
              <Btn onClick={() => setShowUpload(false)} style={{ flex: 1 }}>{t('common.close')}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showNew && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowNew(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 32, width: 500 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 22 }}>
              <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{t('kyc.addNew.title')}</div>
              <button onClick={() => setShowNew(false)} style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 22 }}>✕</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 22 }}>
              {[
                { label: t('kyc.addNew.fullName'), key: "name",    placeholder: t('kyc.addNew.namePlaceholder') },
                { label: t('kyc.fields.country'),  key: "country", placeholder: t('kyc.addNew.countryPlaceholder') },
                { label: t('kyc.fields.notes'),    key: "notes",   placeholder: t('kyc.addNew.notesPlaceholder') },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{f.label.toUpperCase()}</div>
                  <input type="text" placeholder={f.placeholder} value={newForm[f.key] || ""} onChange={e => setNewForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontFamily: "inherit", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                </div>
              ))}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {[
                  { label: t('kyc.fields.type'), key: "type", opts: [t('kyc.fields.individual'), t('kyc.fields.business'), t('kyc.fields.entity')] },
                  { label: t('kyc.fields.riskLevel'), key: "riskLevel", opts: [t('kyc.riskLevels.low'), t('kyc.riskLevels.medium'), t('kyc.riskLevels.high')] },
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.08em", marginBottom: 6 }}>{f.label.toUpperCase()}</div>
                    <select value={newForm[f.key]} onChange={e => setNewForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontFamily: "inherit", fontSize: 13, outline: "none" }}>
                      {f.opts.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="ghost" onClick={() => setShowNew(false)} style={{ flex: 1 }}>{t('common.cancel')}</Btn>
              <Btn onClick={addCustomer} disabled={!newForm.name.trim()} style={{ flex: 1 }}>{t('kyc.addCustomer')}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}